//#region IMPORTS
import {
  isAnyOf,
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
} from "@reduxjs/toolkit";
import {
  InstoreOrder,
  InstoreOrderLine,
  InstoreOrderState,
  InstoreOrderParams,
  FindTemplateRequest,
} from "../../models/instoreOrder";
import { InstoreTemplate } from "../../models/instoreTemplate";
import { FieldValues } from "react-hook-form";
import { RootState } from "../../store/configureStore";
import { toast } from "react-toastify";
import getAxiosParams from "../../helpers/getAxiosParams";
import agent from "../../api/agent";
//#endregion

//#region INITIAL STATE
function initParams(): InstoreOrderParams {
  return {
    page: 1,
    limit: 25,
  };
}

const initialExtraState: InstoreOrderState = {
  instoreOrderLinesLoaded: false,
  instoreOrdersLoaded: false,
  instoreOrder_params: initParams(),
  lastLoadedOrderId: null,
  metaData: undefined,
  loading: false,
  changed: false,
  status: "iddle",
};
//#endregion

//#region CREATE ADAPTERS
const instoreOrdersAdapter = createEntityAdapter<InstoreOrder>();
const instoreOrderLinesAdapter = createEntityAdapter<InstoreOrderLine>();
//#endregion

//#region READ
// Fetch All
export const fetchInstoreOrdersAsync = createAsyncThunk<
  InstoreOrder[],
  void,
  { state: RootState }
>("instoreOrders/fetchInstoreOrdersAsync", async (_, thunkAPI) => {
  const instoreOrderParams = getAxiosParams(
    thunkAPI.getState().instoreOrders.instoreOrder_params
  );
  try {
    const { data, meta } = await agent.InstoreOrders.list(instoreOrderParams);
    thunkAPI.dispatch(setInstoreOrderMetaData(meta));
    return data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response.data.message);
  }
});

// Fetch by id
export const fetchInstoreOrderAsync = createAsyncThunk<
  InstoreOrder,
  number,
  { state: RootState }
>("instoreOrders/fetchInstoreOrderAsync", async (instoreOrder_id, thunkAPI) => {
  try {
    const { data } = await agent.InstoreOrders.detail(instoreOrder_id);
    return data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response.data.message);
  }
});

// Fetch template by order params
export const fetchMatchingInstoreTemplateAsync = createAsyncThunk<
  { template: InstoreTemplate; instore_order_lines: InstoreOrderLine[] },
  FindTemplateRequest,
  { state: RootState }
>(
  "instoreTemplates/fetchMatchingInstoreTemplateAsync",
  async (values, thunkAPI) => {
    try {
      const response = await agent.InstoreOrders.fitTemplate({ ...values });
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data.message);
    }
  }
);
//#endregion

//#region CREATE
export const createInstoreOrderAsync = createAsyncThunk<
  InstoreOrder,
  { values: FieldValues | null }
>("instoreOrders/createInstoreOrderAsync", async ({ values }, thunkAPI) => {
  try {
    const { data } = await agent.InstoreOrders.create({ ...values });
    return data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response.data.message);
  }
});
//#endregion

//#region UPDATE
export const updateInstoreOrderAsync = createAsyncThunk<
  InstoreOrder,
  { id: number; values: FieldValues }
>("instoreOrders/updateInstoreOrderAsync", async ({ id, values }, thunkAPI) => {
  try {
    const { data } = await agent.InstoreOrders.update(id, { ...values });
    return data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response.data.message);
  }
});

export const updateManyInstoreOrderLinesAsync = createAsyncThunk<
  InstoreOrder,
  { instoreOrder_id: number; values: FieldValues }
>(
  "instoreOrders/updateManyInstoreOrderLinesAsync",
  async ({ instoreOrder_id, values }, thunkAPI) => {
    try {
      const { data } = await agent.InstoreOrderLines.updateMany(
        instoreOrder_id,
        { ...values }
      );
      return data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data.message);
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      return !state.instoreOrders.changed;
    },
  }
);
//#endregion

//#region DELETE
export const deleteInstoreOrderAsync = createAsyncThunk<number, { id: number }>(
  "instoreOrders/deleteInstoreOrderAsync",
  async ({ id }, thunkAPI) => {
    try {
      await agent.InstoreOrders.delete(id);
      return id;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data.message);
    }
  }
);
//#endregion

const initialState = {
  orders: instoreOrdersAdapter.getInitialState(),
  orderLines: instoreOrderLinesAdapter.getInitialState(),
  ...initialExtraState,
};

//#region CREATE REDUCER SLICE
export const instoreOrderSlice = createSlice({
  name: "instoreOrders",
  initialState,
  reducers: {
    setInstoreOrderParams: (state, action) => {
      state.instoreOrdersLoaded = false;
      state.instoreOrder_params = {
        ...state.instoreOrder_params,
        ...action.payload,
        page: 1,
      };
    },
    upsertInstoreOrderLineRowLocal: (state, action) => {
      instoreOrderLinesAdapter.upsertOne(state.orderLines, action.payload);
      state.changed = true;
    },
    upsertManyInstoreOrderLineRowLocal: (state, action) => {
      instoreOrderLinesAdapter.upsertMany(state.orderLines, action.payload);
      state.changed = true;
    },
    removeInstoreOrderLineRowLocal: (state, action) => {
      instoreOrderLinesAdapter.removeOne(state.orderLines, action.payload);
      state.changed = true;
    },
    removeManyInstoreOrderLineRowLocal: (state, action) => {
      instoreOrderLinesAdapter.removeMany(state.orderLines, action.payload);
      state.changed = true;
    },
    clearInstoreOrderParams: (state) => {
      state.instoreOrdersLoaded = false;
      state.instoreOrder_params = initParams();
    },
    clearInstoreOrderParam: (state, action) => {
      const newParmams = { ...state.instoreOrder_params };
      delete newParmams[action.payload];
      state.instoreOrdersLoaded = false;
      state.instoreOrder_params = newParmams;
    },
    setInstoreOrderPageNumber: (state, action) => {
      state.instoreOrdersLoaded = false;
      state.instoreOrder_params = {
        ...state.instoreOrder_params,
        ...action.payload,
      };
    },
    setInstoreOrderMetaData: (state, action) => {
      state.metaData = action.payload;
    },
    resetInstoreOrderParams: (state) => {
      state.instoreOrdersLoaded = false;
      state.instoreOrder_params = initParams();
    },
    clearInstoreOrders: () => initialState,
  },
  extraReducers: (builder) => {
    // FETCH instoreOrders
    builder.addCase(fetchInstoreOrdersAsync.pending, (state) => {
      state.loading = true;
      state.status = "pendingFetchInstoreOrders";
    });
    builder.addCase(fetchInstoreOrdersAsync.fulfilled, (state, { payload }) => {
      instoreOrdersAdapter.setAll(state.orders, payload);
      state.instoreOrdersLoaded = true;
      state.lastLoadedOrderId = null;
      state.loading = false;
      state.status = "idle";
    });

    // FETCH InstoreOrder by id and set orderlines
    builder.addCase(fetchInstoreOrderAsync.pending, (state) => {
      state.loading = true;
      state.status = "pendingFetchInstoreOrder";
      state.instoreOrderLinesLoaded = false;
    });
    builder.addCase(fetchInstoreOrderAsync.fulfilled, (state, { payload }) => {
      instoreOrdersAdapter.upsertOne(state.orders, payload);
      instoreOrderLinesAdapter.setAll(
        state.orderLines,
        payload.instore_order_lines
      );
      state.instoreOrderLinesLoaded = true;
      state.lastLoadedOrderId = payload.id;
      state.loading = false;
      state.changed = false;
      state.status = "idle";
    });

    // FETCH matching instoreTemplate by order params and set rows
    builder.addCase(fetchMatchingInstoreTemplateAsync.pending, (state) => {
      state.loading = true;
      state.status = "pendingFetchMatchingInstoreTemplateAsync";
    });
    builder.addCase(
      fetchMatchingInstoreTemplateAsync.fulfilled,
      (state, { payload }) => {
        instoreOrderLinesAdapter.setAll(
          state.orderLines,
          payload.instore_order_lines.map((item, index) => ({
            ...item,
            id: index,
          }))
        );
        state.instoreOrderLinesLoaded = true;
        state.loading = false;
        state.changed = false;
        state.status = "idle";
      }
    );

    // CREATE InstoreOrder
    builder.addCase(createInstoreOrderAsync.pending, (state) => {
      state.loading = true;
      state.status = "PendingCreateInstoreOrder";
    });
    builder.addCase(createInstoreOrderAsync.fulfilled, (state, { payload }) => {
      instoreOrdersAdapter.addOne(state.orders, payload);
      instoreOrderLinesAdapter.setAll(
        state.orderLines,
        payload.instore_order_lines
      );
      toast.success("Instore order created");
      state.loading = false;
      state.changed = false;
      state.status = "idle";
    });

    // UPDATE InstoreOrder
    builder.addCase(updateInstoreOrderAsync.pending, (state, { meta }) => {
      state.loading = true;
      state.status = "PendingUpdateInstoreOrder" + meta.arg.id;
    });
    builder.addCase(updateInstoreOrderAsync.fulfilled, (state, { payload }) => {
      instoreOrdersAdapter.upsertOne(state.orders, payload);
      toast.success(`Instore order ${payload.order_code} updated`);
      state.changed = false;
      state.loading = false;
      state.status = "idle";
    });

    // UPDATE MANY InstoreOrderLines
    builder.addCase(
      updateManyInstoreOrderLinesAsync.pending,
      (state, { meta }) => {
        state.loading = true;
        state.status =
          "PendingUpdateManyInstoreOrderLines" + meta.arg.instoreOrder_id;
      }
    );
    builder.addCase(
      updateManyInstoreOrderLinesAsync.fulfilled,
      (state, { payload }) => {
        instoreOrderLinesAdapter.upsertMany(
          state.orderLines,
          payload.instore_order_lines
        );
        state.changed = false;
        state.loading = false;
        state.status = "idle";
      }
    );

    // DELETE InstoreOrder
    builder.addCase(deleteInstoreOrderAsync.pending, (state) => {
      state.loading = true;
      state.status = "PendingDeleteInstoreOrder";
    });
    builder.addCase(deleteInstoreOrderAsync.fulfilled, (state, { payload }) => {
      instoreOrdersAdapter.removeOne(state.orders, payload);
      state.orderLines = instoreOrderLinesAdapter.getInitialState();
      toast.success("Instore order deleted");
      state.lastLoadedOrderId = null;
      state.loading = false;
      state.changed = false;
      state.status = "idle";
    });

    //#region CRUD REJECTION
    builder.addMatcher(
      isAnyOf(
        fetchInstoreOrderAsync.rejected,
        fetchInstoreOrdersAsync.rejected,
        createInstoreOrderAsync.rejected,
        updateInstoreOrderAsync.rejected,
        deleteInstoreOrderAsync.rejected,
        updateManyInstoreOrderLinesAsync.rejected,
        fetchMatchingInstoreTemplateAsync.rejected
      ),
      (state, { payload }) => {
        state.orderLines = instoreOrderLinesAdapter.getInitialState();
        toast.error(`${payload}`);
        state.lastLoadedOrderId = null;
        state.instoreOrderLinesLoaded = false;
        state.loading = false;
        state.status = "idle";
      }
    );
    //#endregion
  },
});
//#endregion

//#region NORMALISED STATE
export const instoreOrdersSelectors = instoreOrdersAdapter.getSelectors(
  (state: RootState) => state.instoreOrders.orders
);

export const instoreOrderLinesSelectors = instoreOrderLinesAdapter.getSelectors(
  (state: RootState) => state.instoreOrders.orderLines
);
//#endregion

//#region EXPORT
export const {
  removeManyInstoreOrderLineRowLocal,
  upsertManyInstoreOrderLineRowLocal,
  removeInstoreOrderLineRowLocal,
  upsertInstoreOrderLineRowLocal,
  setInstoreOrderPageNumber,
  setInstoreOrderMetaData,
  resetInstoreOrderParams,
  clearInstoreOrderParams,
  clearInstoreOrderParam,
  setInstoreOrderParams,
  clearInstoreOrders,
} = instoreOrderSlice.actions;
//#endregion

export default instoreOrderSlice.reducer;
